import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useClickOutside } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { FiasObject } from 'types/interfaces';

import { BaseFiasUrl } from 'constants/BaseUrl';

import { withDialogFooterSpacing } from '../shared/dialogFooterSpacing';
import { TagsComponent } from '../TagsComponent';

import '../custom-controls/styles/FiasSearchInput.css';
import './FiasAddressExclusionDialogs.css';

const displayStreet = (s: FiasObject) => s.fullName || s.name;
const displayHouse = (h: FiasObject) => h.fullName || h.name;

function StreetExclusionPicker({
  cityId,
  onPick,
  onUnpick,
  disabledIds
}: {
  cityId: number;
  onPick: (street: FiasObject) => void;
  onUnpick: (street: FiasObject) => void;
  disabledIds: Set<number>;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FiasObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const resetSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  }, []);

  const fetchResults = useCallback(
    async (q: string) => {
      if (q.length < 3) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const url = `${BaseFiasUrl}/city/${cityId}/street?name=${encodeURIComponent(q)}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('network');
        const data = await response.json();
        setResults(Array.isArray(data) ? data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [cityId]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 500);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchResults]);

  useClickOutside(wrapperRef, () => {
    setIsOpen(false);
  });

  const handlePick = (item: FiasObject) => {
    if (!item?.objectId) return;
    onPick(item);
  };

  const handleRowActivate = (item: FiasObject) => {
    if (!item?.objectId) return;
    if (disabledIds.has(item.objectId)) onUnpick(item);
    else handlePick(item);
  };

  const showPanel = isOpen;

  return (
    <div className="fias-search-wrapper fias-exclusion-picker" ref={wrapperRef}>
      <div className="flex align-items-stretch gap-2">
        <div className="input-container flex-grow-1 min-w-0">
          <InputText
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Начните вводить улицу..."
            className="w-full"
            autoComplete="off"
            disabled={loading}
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={resetSearch}
              className="clear-button"
              aria-label="Очистить поиск"
            >
              <i className="pi pi-times" />
            </button>
          )}
        </div>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          className="p-button-outlined flex-shrink-0"
          onClick={resetSearch}
          disabled={!query.length && !results.length && !loading}
          tooltip="Сбросить поиск"
          tooltipOptions={{ position: 'top' }}
          aria-label="Сбросить поиск"
        />
      </div>

      {showPanel && (
        <div className="dropdown-results fias-exclusion-picker__panel w-full">
          {loading && query.length >= 3 ? (
            <div className="loading-message">Загрузка...</div>
          ) : query.length > 0 && query.length < 3 ? (
            <div className="no-results">
              Введите не менее 3 символов для поиска
            </div>
          ) : query.length >= 3 && results.length > 0 ? (
            results.map((item) => {
              const taken = disabledIds.has(item.objectId);
              return (
                <div
                  key={item.objectId}
                  role="button"
                  tabIndex={0}
                  aria-pressed={taken}
                  className={`fias-exclusion-picker__row result-item fias-exclusion-picker__row--pickable ${
                    taken ? 'fias-exclusion-picker__row--selected' : ''
                  }`}
                  onClick={() => handleRowActivate(item)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    handleRowActivate(item);
                  }}
                >
                  <span className="fias-exclusion-picker__row-label">
                    {displayStreet(item)}
                  </span>
                </div>
              );
            })
          ) : query.length >= 3 ? (
            <div className="no-results">Ничего не найдено</div>
          ) : (
            <div className="no-results">
              Введите не менее 3 символов для поиска
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HouseExclusionPicker({
  houses,
  loading,
  onPick,
  onUnpick,
  disabledIds,
  onReload
}: {
  houses: FiasObject[];
  loading: boolean;
  onPick: (house: FiasObject) => void;
  onUnpick: (house: FiasObject) => void;
  disabledIds: Set<number>;
  onReload: () => void;
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return houses;
    return houses.filter((h) => {
      const n = (h.name || '').toLocaleLowerCase();
      const fn = (h.fullName || '').toLocaleLowerCase();
      return n.includes(q) || fn.includes(q);
    });
  }, [houses, query]);

  const resetSearch = useCallback(() => {
    setQuery('');
    setIsOpen(false);
  }, []);

  useClickOutside(wrapperRef, () => {
    setIsOpen(false);
  });

  const showPanel = isOpen;

  const handleRowActivate = (item: FiasObject) => {
    if (!item?.objectId) return;
    if (disabledIds.has(item.objectId)) onUnpick(item);
    else onPick(item);
  };

  return (
    <div className="fias-search-wrapper fias-exclusion-picker" ref={wrapperRef}>
      <div className="flex align-items-stretch gap-2">
        <div className="input-container flex-grow-1 min-w-0">
          <InputText
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={
              loading ? 'Загрузка…' : 'Поиск по номеру или названию дома…'
            }
            className="w-full"
            autoComplete="off"
            disabled={loading}
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={resetSearch}
              className="clear-button"
              aria-label="Очистить поиск"
            >
              <i className="pi pi-times" />
            </button>
          )}
        </div>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          className="p-button-outlined flex-shrink-0"
          onClick={resetSearch}
          disabled={!query.length}
          tooltip="Сбросить поиск"
          tooltipOptions={{ position: 'top' }}
          aria-label="Сбросить поиск"
        />
        <Button
          type="button"
          icon="pi pi-refresh"
          className="p-button-outlined flex-shrink-0"
          onClick={onReload}
          loading={loading}
          tooltip="Обновить список домов"
          tooltipOptions={{ position: 'top' }}
          aria-label="Обновить список домов"
        />
      </div>

      {showPanel && (
        <div className="dropdown-results fias-exclusion-picker__panel w-full">
          {loading ? (
            <div className="loading-message">Загрузка...</div>
          ) : !houses.length ? (
            <div className="no-results">Нет домов для этой улицы</div>
          ) : filtered.length > 0 ? (
            filtered.map((item) => {
              const taken = disabledIds.has(item.objectId);
              return (
                <div
                  key={item.objectId}
                  role="button"
                  tabIndex={0}
                  aria-pressed={taken}
                  className={`fias-exclusion-picker__row result-item fias-exclusion-picker__row--pickable ${
                    taken ? 'fias-exclusion-picker__row--selected' : ''
                  }`}
                  onClick={() => handleRowActivate(item)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    handleRowActivate(item);
                  }}
                >
                  <span className="fias-exclusion-picker__row-label">
                    {displayHouse(item)}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="no-results">Нет совпадений по фильтру</div>
          )}
        </div>
      )}
    </div>
  );
}

export function FiasStreetExclusionDialog({
  visible,
  onHide,
  cityId,
  initialExcluded,
  onApply
}: {
  visible: boolean;
  onHide: () => void;
  cityId: number;
  initialExcluded: FiasObject[];
  onApply: (streets: FiasObject[]) => void;
}) {
  const [draft, setDraft] = useState<FiasObject[]>([]);

  useEffect(() => {
    if (visible) {
      setDraft(initialExcluded?.length ? [...initialExcluded] : []);
    }
  }, [visible, initialExcluded]);

  const disabledStreetIds = useMemo(
    () => new Set(draft.map((s) => s.objectId)),
    [draft]
  );

  const addStreet = (street: FiasObject) => {
    if (!street?.objectId) return;
    setDraft((prev) => {
      if (prev.some((s) => s.objectId === street.objectId)) return prev;
      return [...prev, street];
    });
  };

  const removeStreet = (objectId: number) => {
    setDraft((prev) => prev.filter((s) => s.objectId !== objectId));
  };

  const clearDraft = () => setDraft([]);

  const handleApply = () => {
    onApply(draft);
    onHide();
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button type="button" label="Отмена" severity="secondary" onClick={onHide} />
      <Button type="button" label="Применить" onClick={handleApply} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Исключение улиц"
      className="fias-exclusion-dialog"
      style={{ width: 'min(560px, 95vw)' }}
      footer={footer}
      draggable={false}
      pt={withDialogFooterSpacing()}
    >
      <div className="flex flex-column gap-3">
        <div>
          <label className="block mb-2 text-sm font-medium">
            Поиск и выбор улиц
          </label>
          <StreetExclusionPicker
            cityId={cityId}
            onPick={addStreet}
            onUnpick={(s) => removeStreet(s.objectId)}
            disabledIds={disabledStreetIds}
          />
        </div>

        <div>
          <div className="flex align-items-center justify-content-between gap-2 mb-2">
            <span className="text-sm text-color-secondary">Исключённые улицы</span>
            {draft.length > 0 && (
              <Button
                type="button"
                label="Сбросить список"
                text
                severity="secondary"
                className="p-0 text-sm"
                onClick={clearDraft}
              />
            )}
          </div>
          {draft.length === 0 ? (
            <span className="text-color-secondary text-sm">Нет выбранных улиц</span>
          ) : (
            <div className="fias-exclusion-tags">
              <TagsComponent
                key={draft.map((d) => d.objectId).join(',')}
                initialTagsItems={draft.map((d) => ({
                  id: d.objectId,
                  label: displayStreet(d)
                }))}
                onDelete={(id) => removeStreet(Number(id))}
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export function FiasHouseExclusionDialog({
  visible,
  onHide,
  cityId,
  streetId,
  initialExcluded,
  onApply
}: {
  visible: boolean;
  onHide: () => void;
  cityId: number;
  streetId: number;
  initialExcluded: FiasObject[];
  onApply: (houses: FiasObject[]) => void;
}) {
  const [draft, setDraft] = useState<FiasObject[]>([]);
  const [houses, setHouses] = useState<FiasObject[]>([]);
  const [loadingHouses, setLoadingHouses] = useState(false);

  useEffect(() => {
    if (visible) {
      setDraft(initialExcluded?.length ? [...initialExcluded] : []);
    }
  }, [visible, initialExcluded]);

  const loadHouses = useCallback(async () => {
    if (!cityId || !streetId) return;
    setLoadingHouses(true);
    try {
      const response = await fetch(
        `${BaseFiasUrl}/city/${cityId}/street/${streetId}/house`
      );
      const data = response.ok ? await response.json() : [];
      setHouses(Array.isArray(data) ? data : []);
    } catch {
      setHouses([]);
    } finally {
      setLoadingHouses(false);
    }
  }, [cityId, streetId]);

  useEffect(() => {
    if (!visible || !cityId || !streetId) return;
    loadHouses();
  }, [visible, cityId, streetId, loadHouses]);

  const disabledHouseIds = useMemo(
    () => new Set(draft.map((h) => h.objectId)),
    [draft]
  );

  const addHouse = (house: FiasObject) => {
    if (!house?.objectId) return;
    setDraft((prev) => {
      if (prev.some((h) => h.objectId === house.objectId)) return prev;
      return [...prev, house];
    });
  };

  const removeHouse = (objectId: number) => {
    setDraft((prev) => prev.filter((h) => h.objectId !== objectId));
  };

  const clearDraft = () => setDraft([]);

  const handleApply = () => {
    onApply(draft);
    onHide();
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button type="button" label="Отмена" severity="secondary" onClick={onHide} />
      <Button type="button" label="Применить" onClick={handleApply} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Исключение домов"
      className="fias-exclusion-dialog"
      style={{ width: 'min(560px, 95vw)' }}
      footer={footer}
      draggable={false}
      pt={withDialogFooterSpacing()}
    >
      <div className="flex flex-column gap-3">
        <div>
          <label className="block mb-2 text-sm font-medium">
            Поиск и выбор домов
          </label>
          <HouseExclusionPicker
            houses={houses}
            loading={loadingHouses}
            onPick={addHouse}
            onUnpick={(h) => removeHouse(h.objectId)}
            disabledIds={disabledHouseIds}
            onReload={loadHouses}
          />
        </div>

        <div>
          <div className="flex align-items-center justify-content-between gap-2 mb-2">
            <span className="text-sm text-color-secondary">Исключённые дома</span>
            {draft.length > 0 && (
              <Button
                type="button"
                label="Сбросить список"
                text
                severity="secondary"
                className="p-0 text-sm"
                onClick={clearDraft}
              />
            )}
          </div>
          {draft.length === 0 ? (
            <span className="text-color-secondary text-sm">Нет выбранных домов</span>
          ) : (
            <div className="fias-exclusion-tags">
              <TagsComponent
                key={draft.map((d) => d.objectId).join(',')}
                initialTagsItems={draft.map((d) => ({
                  id: d.objectId,
                  label: displayHouse(d)
                }))}
                onDelete={(id) => removeHouse(Number(id))}
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
